import React from 'react';
import { CheckCircle, RotateCcw, Copy } from 'lucide-react';
import { QuizCtaVariant, TREE_IMAGES } from '../utils/quizCtaGenerator';

type CTA = { cta: { category_header: string; headline: string; description: string; action_button: string }; [key: string]: any };

interface QuizCtaPreviewCardsProps {
  ctaVariants: Record<string, CTA>;
  /** Compact mode hides the Change CTA button, description, tree image, HTML code, and URL preview */
  compact?: boolean;
  onCycleCTAButton?: (position: 'beginning' | 'middle' | 'end') => void;
  getStyledCtaHtml?: (position: 'beginning' | 'middle' | 'end') => string;
  getQuizUrl?: (position: string) => string;
  ctaCopySuccess?: string | null;
  onCopyCtaToClipboard?: (html: string, key: string) => void;
}

const POSITION_COLORS: Record<string, { bg: string; text: string }> = {
  beginning: { bg: '#dbeafe', text: '#1e40af' },
  middle: { bg: '#fef3c7', text: '#b45309' },
  end: { bg: '#dcfce7', text: '#16a34a' },
};

const QuizCtaPreviewCards: React.FC<QuizCtaPreviewCardsProps> = ({
  ctaVariants,
  compact = false,
  onCycleCTAButton,
  getStyledCtaHtml,
  getQuizUrl,
  ctaCopySuccess,
  onCopyCtaToClipboard,
}) => {
  const positionKeys = Object.keys(ctaVariants);

  return (
    <div style={{ marginTop: compact ? undefined : '1rem', marginBottom: compact ? '2rem' : undefined }}>
      <h4 style={{
        fontSize: '0.875rem',
        fontWeight: '600',
        margin: compact ? '0 0 1rem 0' : '0 0 1.5rem 0',
        color: '#374151',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
      }}>
        <CheckCircle size={14} style={{ color: '#16a34a' }} />
        Generated Quiz CTAs
      </h4>

      <div style={{ display: 'grid', gap: compact ? '1rem' : '1.5rem' }}>
        {Object.entries(ctaVariants).map(([position, ctaData]) => {
          const colors = POSITION_COLORS[position] || POSITION_COLORS.end;
          const cta = (ctaData as QuizCtaVariant).cta;

          return (
            <div key={position} style={{
              border: '1px solid #e5e7eb',
              borderRadius: '0.75rem',
              padding: compact ? '0.75rem' : '1rem',
              backgroundColor: '#fafafa',
              overflow: 'hidden',
              maxWidth: '100%',
            }}>
              {/* Position Badge + optional Change CTA Button */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: compact ? '0.5rem' : '1rem' }}>
                <div style={{
                  padding: compact ? '0.2rem 0.5rem' : '0.25rem 0.75rem',
                  backgroundColor: colors.bg,
                  color: colors.text,
                  borderRadius: compact ? '0.25rem' : '0.375rem',
                  fontSize: compact ? '0.7rem' : '0.75rem',
                  fontWeight: '600',
                  textTransform: 'uppercase' as const,
                }}>
                  {position === 'end' ? 'ending' : position}
                </div>

                {!compact && onCycleCTAButton && (
                  <button
                    onClick={() => onCycleCTAButton(position as 'beginning' | 'middle' | 'end')}
                    style={{
                      padding: '0.375rem 0.625rem',
                      backgroundColor: '#f3f4f6',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.375rem',
                      fontSize: '0.7rem',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                    }}
                    title="Cycle to a different CTA button text"
                  >
                    <RotateCcw size={11} />
                    Change CTA
                  </button>
                )}
              </div>

              {/* Green CTA Preview Card */}
              <div style={{
                background: '#4E960C',
                padding: compact ? '0.75rem' : '1.25rem',
                borderRadius: compact ? '0.5rem' : '0.75rem',
                marginBottom: compact ? undefined : '1rem',
                overflow: 'hidden',
              }}>
                <div style={compact ? undefined : { display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={compact ? undefined : { flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: compact ? '0.65rem' : '0.7rem',
                      fontWeight: '700',
                      color: '#FFD93D',
                      marginBottom: compact ? '0.25rem' : '0.375rem',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase' as const,
                    }}>
                      {cta.category_header}
                    </div>

                    {compact ? (
                      <div style={{ fontSize: '0.9rem', fontWeight: '800', color: '#FFFAEE', marginBottom: '0.25rem' }}>
                        {cta.headline}
                      </div>
                    ) : (
                      <h4 style={{ fontSize: '1.1rem', fontWeight: '800', color: '#FFFAEE', margin: '0 0 0.5rem 0', lineHeight: '1.3' }}>
                        {cta.headline}
                      </h4>
                    )}

                    {!compact && (
                      <p style={{ fontSize: '0.8rem', color: '#FFFAEE', lineHeight: '1.5', margin: '0 0 0.75rem 0', opacity: 0.92 }}>
                        {cta.description}
                      </p>
                    )}

                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: compact ? '0.25rem' : '0.375rem',
                      padding: compact ? '0.375rem 0.75rem' : '0.625rem 1.25rem',
                      backgroundColor: '#FFD93D',
                      color: '#1a1a1a',
                      borderRadius: compact ? '0.375rem' : '0.5rem',
                      fontSize: compact ? '0.75rem' : '0.875rem',
                      fontWeight: '700',
                    }}>
                      {cta.action_button}
                      <span>{compact ? ' →' : '→'}</span>
                    </div>
                  </div>

                  {/* Mini tree image preview (full mode only) */}
                  {!compact && (
                    <div style={{
                      width: '110px',
                      height: '132px',
                      flexShrink: 0,
                      borderRadius: '8px',
                      overflow: 'hidden',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    }}>
                      <img
                        src={TREE_IMAGES[positionKeys.indexOf(position) % TREE_IMAGES.length].url}
                        alt="Citrus tree"
                        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* HTML Code Preview + Quiz URL (full mode only) */}
              {!compact && getStyledCtaHtml && onCopyCtaToClipboard && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#374151' }}>
                      Styled HTML Code:
                    </div>
                    <button
                      onClick={() => onCopyCtaToClipboard(getStyledCtaHtml(position as 'beginning' | 'middle' | 'end'), `${position}_shortcode`)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: ctaCopySuccess === `${position}_shortcode` ? '#dcfce7' : '#6b7280',
                        color: ctaCopySuccess === `${position}_shortcode` ? '#16a34a' : 'white',
                        border: 'none',
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                      }}
                    >
                      {ctaCopySuccess === `${position}_shortcode` ? (
                        <><CheckCircle size={10} /> Copied!</>
                      ) : (
                        <><Copy size={10} /> Copy</>
                      )}
                    </button>
                  </div>
                  <div style={{
                    backgroundColor: '#f3f4f6',
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    fontSize: '0.625rem',
                    fontFamily: 'monospace',
                    color: '#374151',
                    whiteSpace: 'pre-wrap',
                    border: '1px solid #e5e7eb',
                    maxHeight: '6rem',
                    overflow: 'auto',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                  }}>
                    {getStyledCtaHtml(position as 'beginning' | 'middle' | 'end')}
                  </div>
                </>
              )}

              {/* Quiz URL Preview (full mode only) */}
              {!compact && getQuizUrl && (
                <div style={{ marginTop: '0.75rem' }}>
                  <div style={{
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    color: '#374151',
                    marginBottom: '0.25rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                  }}>
                    🔗 Quiz URL:
                  </div>
                  <div style={{
                    backgroundColor: '#f0fdf4',
                    padding: '0.5rem',
                    borderRadius: '0.375rem',
                    fontSize: '0.625rem',
                    fontFamily: 'monospace',
                    color: '#166534',
                    border: '1px solid #bbf7d0',
                    wordBreak: 'break-all',
                    overflow: 'hidden',
                    maxWidth: '100%',
                  }}>
                    {getQuizUrl(position)}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuizCtaPreviewCards;
